import React, { useEffect, useState } from 'react';
import './MemoryGame.css';

const animalImages = {
  S: ['panda', 'elephant', 'hippo', 'rhino'],
  M: ['panda', 'elephant', 'hippo', 'rhino', 'ant', 'frog'],
  L: ['panda', 'elephant', 'hippo', 'rhino', 'ant', 'frog', 'gorilla', 'eagle'],
  XL: ['panda', 'elephant', 'hippo', 'rhino', 'ant', 'frog', 'gorilla', 'eagle', 'falcon', 'lion'],
};

const sizeOptions = { S: 8, M: 12, L: 16, XL: 20 };

function shuffleArray(array) {
  return array
    .map((value, index) => ({ value, sort: Math.random(), index }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value);
}

function MemoryGame() {
  const [selectedSize, setSelectedSize] = useState(null);
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [flipCount, setFlipCount] = useState(0);
  const [showWin, setShowWin] = useState(false);
  const [session, setSession] = useState(0);

  const startGame = (sizeKey) => {
    const selectedAnimals = animalImages[sizeKey] || [];
    const gameCards = shuffleArray([...selectedAnimals, ...selectedAnimals]).map((animal, index) => ({
      id: `${sizeKey}-${index}`,
      animal,
      flipped: false,
      matched: false,
    }));

    setSession((prev) => prev + 1);
    setSelectedSize(sizeKey);
    setCards(gameCards);
    setFlippedCards([]);
    setDisabled(false);
    setFlipCount(0);
    setShowWin(false);
  };

  const handleCardClick = (index) => {
    if (!cards.length || disabled) return;
    const chosenCard = cards[index];
    if (!chosenCard || chosenCard.flipped || chosenCard.matched) return;

    const updatedCards = cards.map((card, idx) =>
      idx === index ? { ...card, flipped: true } : card
    );
    const updatedFlippedCards = [...flippedCards, index];

    setCards(updatedCards);
    setFlippedCards(updatedFlippedCards);

    if (updatedFlippedCards.length === 2) {
      const sessionAtClick = session;
      setDisabled(true);
      setFlipCount((prev) => prev + 1);

      const [firstIdx, secondIdx] = updatedFlippedCards;
      const firstCard = updatedCards[firstIdx];
      const secondCard = updatedCards[secondIdx];

      if (firstCard.animal === secondCard.animal) {
        setTimeout(() => {
          setCards((prevCards) => {
            if (session !== sessionAtClick) return prevCards;
            return prevCards.map((card, idx) =>
              idx === firstIdx || idx === secondIdx
                ? { ...card, matched: true }
                : card
            );
          });
          if (session === sessionAtClick) {
            setFlippedCards([]);
            setDisabled(false);
          }
        }, 500);
      } else {
        setTimeout(() => {
          setCards((prevCards) => {
            if (session !== sessionAtClick) return prevCards;
            return prevCards.map((card, idx) =>
              idx === firstIdx || idx === secondIdx
                ? { ...card, flipped: false }
                : card
            );
          });
          if (session === sessionAtClick) {
            setFlippedCards([]);
            setDisabled(false);
          }
        }, 750);
      }
    }
  };

  useEffect(() => {
    if (cards.length && cards.every((card) => card.matched)) {
      setShowWin(true);
    }
  }, [cards]);

  const matchedPairs = cards.filter((card) => card.matched).length / 2;
  const totalPairs = cards.length / 2;
  const boardLabel = selectedSize ? `${selectedSize} board` : 'board';

  return (
    <section className="mg-game" aria-live="polite">
      <div className="mg-toolbar">
        <div className="mg-controls">
          <span className="mg-label">Board size</span>
          <div className="mg-size-options">
            {Object.keys(sizeOptions).map((sizeKey) => (
              <button
                key={sizeKey}
                type="button"
                className={`mg-size-btn ${selectedSize === sizeKey ? 'active' : ''}`}
                onClick={() => startGame(sizeKey)}
              >
                {sizeKey}
              </button>
            ))}
          </div>
        </div>

        {cards.length > 0 && (
          <div className="mg-score">
            <div className="mg-score-label">Flips</div>
            <div className="mg-score-value">{flipCount}</div>
            <div className="mg-score-subtext">
              {matchedPairs}/{totalPairs} pairs matched
            </div>
          </div>
        )}
      </div>

      {cards.length === 0 ? (
        <div className="mg-placeholder">
          <p>Select a size to deal a fresh deck.</p>
        </div>
      ) : (
        <div className="mg-grid" data-size={selectedSize || 'S'}>
          {cards.map((card, idx) => {
            const isRevealed = card.flipped || card.matched;
            return (
              <button
                key={card.id}
                type="button"
                className={`mg-card ${isRevealed ? 'is-flipped' : ''} ${card.matched ? 'is-matched' : ''}`}
                onClick={() => handleCardClick(idx)}
                disabled={card.matched || disabled}
                aria-label={card.matched ? `${card.animal} matched` : `Flip card ${idx + 1}`}
              >
                <div className="mg-card-face">
                  {isRevealed ? (
                    <img
                      src={`${process.env.PUBLIC_URL}/images/animals/${card.animal}.png`}
                      alt={card.animal}
                    />
                  ) : (
                    <span className="mg-card-back-symbol" aria-hidden="true">
                      ?
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {showWin && (
        <div className="mg-overlay" role="dialog" aria-modal="true" aria-label="Game completed">
          <div className="mg-modal">
            <p className="mg-eyebrow">Nicely done</p>
            <h2 className="mg-modal-title">All pairs matched!</h2>
            <p className="mg-modal-text">
              You cleared the {boardLabel} in {flipCount} flips.
            </p>
            <div className="mg-modal-actions">
              {selectedSize && (
                <button className="mg-primary-btn" type="button" onClick={() => startGame(selectedSize)}>
                  Play again
                </button>
              )}
              <button className="mg-ghost-btn" type="button" onClick={() => setShowWin(false)}>
                Keep browsing
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default MemoryGame;
